import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Facebook, Instagram, Phone } from "lucide-react";
import Logo from "./Logo";
import { useContent } from "../content-context";
import { scrollToSection } from "../lib/scroll";
import { EASE } from "./Reveal";

export default function Navbar({ ready }) {
  const { nav, site, social } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToSection(href), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 transition-colors duration-500 ${
          open ? "z-[70]" : "z-50"
        } ${scrolled && !open ? "border-b border-gold/15 bg-navy/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`}
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-10">
          <Link
            to="/"
            onClick={() => setOpen(false)}
            data-testid="nav-logo"
            className="group flex items-center gap-3"
            aria-label="Collanmore Island Lodge — home"
          >
            <Logo className="h-10 w-10 transition-transform duration-500 group-hover:rotate-[15deg]" />
            <span className="hidden flex-col leading-none sm:flex">
              <span className="font-display text-lg tracking-[0.14em] text-ivory">COLLANMORE</span>
              <span className="text-[9px] uppercase tracking-widest2 text-gold-2">Island Lodge</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {nav.slice(0, 5).map((n) =>
              n.children ? (
                <div key={n.to} className="group relative">
                  <Link
                    to={n.to}
                    data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="group relative flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory/80 transition-colors duration-300 hover:text-gold-2"
                  >
                    {n.label}
                    <svg className="h-2.5 w-2.5 transition-transform duration-300 group-hover:rotate-180" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-500 group-hover:w-full" />
                  </Link>
                  <div className="pointer-events-none absolute left-1/2 top-full z-50 w-60 -translate-x-1/2 pt-5 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:opacity-100">
                    <div className="border border-gold/25 bg-navy/95 py-2 shadow-[0_24px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-drop-${c.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                          className="block px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory/75 transition-colors hover:bg-gold/10 hover:text-gold-2"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="group relative text-[11px] font-medium uppercase tracking-[0.22em] text-ivory/80 transition-colors duration-300 hover:text-gold-2"
                >
                  {n.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-500 group-hover:w-full" />
                </Link>
              )
            )}
            <div className="ml-2 flex items-center gap-3">
              <a
                href={social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Collanmore Island Lodge on Instagram"
                data-testid="nav-instagram"
                className="flex h-10 w-10 items-center justify-center border border-ivory/25 text-ivory/80 transition-all duration-300 hover:border-gold hover:text-gold-2"
              >
                <Instagram className="h-4 w-4" />
              </a>
              {social.facebook && (
                <a
                  href={social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Collanmore Island Lodge on Facebook"
                  data-testid="nav-facebook"
                  className="flex h-10 w-10 items-center justify-center border border-ivory/25 text-ivory/80 transition-all duration-300 hover:border-gold hover:text-gold-2"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
            </div>
            <Link
              to="/contact/"
              data-testid="nav-enquire-button"
              className="ml-2 border border-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-2 transition-all duration-500 hover:bg-gold hover:text-navy"
            >
              Enquire Now
            </Link>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="relative z-[60] flex h-11 w-11 flex-col items-center justify-center gap-1.5 lg:hidden"
          >
            <span className={`h-px w-7 bg-ivory transition-transform duration-500 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
            <span className={`h-px w-7 bg-ivory transition-transform duration-500 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[55] flex flex-col bg-navy"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
          >
            <div className="flex flex-1 flex-col justify-center gap-1 px-8 pt-20">
              {nav.map((n, i) => (
                <motion.div key={n.to} className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.15 + i * 0.05 }}
                    className="group flex items-baseline gap-4 border-b border-ivory/10 py-3.5"
                  >
                    <Link
                      to={n.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                      className="flex flex-1 items-baseline gap-4"
                    >
                      <span className="text-[10px] tracking-widest2 text-gold">0{i + 1}</span>
                      <span className="font-display text-3xl text-ivory transition-colors group-hover:text-gold-2">
                        {n.label}
                      </span>
                    </Link>
                  </motion.div>
                  {n.children && (
                    <div className="pb-2">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${c.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                          className="block py-1.5 pl-10 text-sm font-light tracking-wide text-ivory/70 transition-colors hover:text-gold-2"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            <motion.div
              className="flex flex-col gap-4 px-8 pb-24 lg:pb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Link
                to="/contact/"
                onClick={() => setOpen(false)}
                data-testid="mobile-enquire-button"
                className="flex items-center justify-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-navy"
              >
                Enquire Now <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href={site.phoneHref} data-testid="mobile-phone-link" className="flex items-center justify-center gap-2 text-sm text-coastal">
                <Phone className="h-4 w-4 text-gold" /> {site.phoneDisplay}
              </a>
              <div className="flex items-center justify-center gap-4 pt-2">
                <a
                  href={social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Collanmore Island Lodge on Instagram"
                  data-testid="mobile-instagram"
                  className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-ivory/80 transition-all duration-300 hover:border-gold hover:text-gold-2"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                {social.facebook && (
                  <a
                    href={social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Collanmore Island Lodge on Facebook"
                    data-testid="mobile-facebook"
                    className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-ivory/80 transition-all duration-300 hover:border-gold hover:text-gold-2"
                  >
                    <Facebook className="h-4 w-4" />
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
